import { ApiService } from 'react-angle-dashboard-components'
import { groupService } from '..'
import { Employee, EmployeeDTO } from '../../interfaces/employee'

const mapEmployeesToDTO = (
  employees: Employee[],
  isUsingDOB: boolean
): EmployeeDTO[] =>
  employees.map((emp) => ({
    employee_index: emp.id || 0,
    first_name: emp.name,
    has_spouse: emp.spouse,
    children_count: emp.children,
    age: isUsingDOB ? null : (emp.age as number),
    date_of_birth: isUsingDOB ? emp.dob : null,
    spouse_age: isUsingDOB ? null : (emp.spouseAge as number) || null,
    spouse_date_of_birth: isUsingDOB && emp.spouse ? emp.spouseDob : null
  }))

const mapDTOToEmployees = (employeesDTO: EmployeeDTO[]): Employee[] => {
  return employeesDTO.map((employee) => ({
    id: employee.employee_index,
    name: employee.first_name,
    age: employee.age || 35,
    dob: employee.date_of_birth,
    spouse: employee.has_spouse,
    children: employee.children_count,
    spouseAge: employee.has_spouse ? employee.spouse_age : undefined,
    spouseDob: employee.has_spouse ? employee.spouse_date_of_birth : undefined
  }))
}

const mapDTOtoEmployeePlans = (data: any) => {
  const employeePlans = { ...data }
  for (const employeeIdx in employeePlans) {
    const plans = employeePlans[employeeIdx].map((plan: any) => {
      // total cost to cover dependents
      const totalPremiumDependent =
        plan.employer_premium_dependent + plan.employee_premium_dependent
      // total cost to cover employee
      const totalPremiumEmployee =
        plan.employer_premium_employee + plan.employee_premium_employee

      // total cost of plan
      const totalPremium = totalPremiumDependent + totalPremiumEmployee

      // total cost for employer
      const totalEmployerPremium =
        plan.employer_premium_employee + plan.employer_premium_dependent

      // total cost for employee
      const totalEmployeePremium =
        plan.employee_premium_employee + plan.employee_premium_dependent

      return {
        employerPremiumEmployee: plan.employer_premium_employee,
        employerPremiumDependent: plan.employer_premium_dependent,
        employeePremiumEmployee: plan.employee_premium_employee,
        employeePremiumDependent: plan.employee_premium_dependent,
        totalPremiumDependent,
        totalPremiumEmployee,
        totalPremium,
        totalEmployeePremium,
        totalEmployerPremium,
        planName: plan.plan_name,
        tier: plan.metal_tier
      }
    })

    if (!plans.length) {
      employeePlans[employeeIdx] = {
        plans: [],
        minEmployerCost: 0,
        maxEmployerCost: 0,
        minEmployeeCost: 0,
        maxEmployeeCost: 0
      }
    } else {
      const maxPlan = plans[0]
      const minPlan = plans[plans.length - 1]

      employeePlans[employeeIdx] = {
        plans,
        minEmployerCost: minPlan.totalEmployerPremium,
        maxEmployerCost: maxPlan.totalEmployerPremium,
        minEmployeeCost: minPlan.totalEmployeePremium,
        maxEmployeeCost: maxPlan.totalEmployeePremium
      }
    }
  }
  return employeePlans
}

export class EmployeeService extends ApiService {
  createEmployees(employees: Employee[], isUsingDOB: boolean) {
    const promises = [
      this.request({
        method: 'post',
        url: '/employees',
        data: { employees: mapEmployeesToDTO(employees, isUsingDOB) },
        credentials: true
      }),
      groupService.modifyGroup({ total_individuals: employees.length })
    ]

    return Promise.all(promises).then((r) => r)
  }

  getEmployeePlans() {
    return this.request({
      method: 'get',
      url: `/employees/plans`,
      credentials: true
    }).then((r) => mapDTOtoEmployeePlans(r.plans))
  }

  getEmployees() {
    return this.request({
      method: 'get',
      url: '/employees',
      credentials: true
    }).then((r: { employees: EmployeeDTO[] }) => {
      return {
        employees: mapDTOToEmployees(r.employees),
        isUsingDOB:
          Boolean(r.employees.length > 0) &&
          Boolean(r.employees[0].date_of_birth)
      }
    })
  }
}
